import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResourceRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiRequestOptions, TypedResourceRequest } from '../Interfaces/api-request.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly _http = inject(HttpClient);
  private readonly _host = environment.api.host;

  get<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this._buildRequest<T>(resource, 'GET', options);
  }

  post<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this._buildRequest<T>(resource, 'POST', options);
  }

  put<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this._buildRequest<T>(resource, 'PUT', options);
  }

  patch<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this._buildRequest<T>(resource, 'PATCH', options);
  }

  delete<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this._buildRequest<T>(resource, 'DELETE', options);
  }

  run<T>(request: HttpResourceRequest): Observable<T> {
    return this._http.request<T>(this._methodOf(request), request.url, {
      body: request.body,
      params: request.params,
      headers: request.headers as HttpHeaders | Record<string, string | string[]> | undefined,
      responseType: 'json',
    });
  }

  runAndForget(request: HttpResourceRequest): void {
    this.run(request).subscribe({ error: () => undefined });
  }

  download(request: HttpResourceRequest): Observable<Blob> {
    return this._http.request(this._methodOf(request), request.url, {
      params: request.params,
      headers: request.headers as HttpHeaders | Record<string, string | string[]> | undefined,
      responseType: 'blob',
    });
  }

  private _methodOf(request: HttpResourceRequest): string {
    return request.method ?? 'GET';
  }

  private _buildRequest<T>(
    resource: string,
    method: HttpResourceRequest['method'],
    options: ApiRequestOptions,
  ): TypedResourceRequest<T> {
    const path = resource.startsWith('/') ? resource.slice(1) : resource;

    return {
      url: `${this._host}/${path}`,
      method,
      params: options.params,
      body: options.body,
      headers: this._buildHeaders(options),
    } as TypedResourceRequest<T>;
  }

  private _buildHeaders(
    options: ApiRequestOptions,
  ): HttpHeaders | Record<string, string | string[]> {
    let headers = options.headers ?? new HttpHeaders();

    if (options.skipLoading) {
      headers = this._appendHeader(headers, 'skip-loading', 'true');
    }

    if (options.skipError) {
      headers = this._appendHeader(headers, 'skip-error', 'true');
    }

    return headers;
  }

  private _appendHeader(
    headers: HttpHeaders | Record<string, string | string[]>,
    name: string,
    value: string,
  ): HttpHeaders | Record<string, string | string[]> {
    return headers instanceof HttpHeaders
      ? headers.append(name, value)
      : { ...headers, [name]: value };
  }
}
